/**
 * Global CONFIG object injected via Vite `define`, merged with remote
 * configuration and exported for use.
 */

import mapboxgl from 'mapbox-gl'

import { stripMobileSuffix } from './utils/authPortalHosts'

export interface IAppConfig {
  // client section
  REDIRECT_CONFIG: {
    isDesktopVersion: boolean
    redirects: Array<{
      from: string
      to: string
    }>
  }

  SUBDOMAIN: string
  BASE_URL: string

  // remote section
  ENVIRONMENT: 'development' | 'qa' | 'production' | 'staging'
  GOOGLE_ANALYTICS_ID: string

  MAPBOX_TOKEN: string
  IPAPI_KEY: string
  TENANT_ID: string
  AUTH_EXTERNAL_URL: string
  WORKFLOW_AI_BACKEND_URL?: string
  WORKFLOW_AI_BACKEND_TOKEN?: string
  DESKTOP_PORT?: number | string
  MOBILE_PORT?: number | string
  LOGIN_PORT?: number | string

  // build metadata
  VERSION: string
  BUILD: string
  REVISION: string
  DEPLOYMENT_VERSION: string

  // client service section
  READY: Promise<void>
}

const REMOTE_CONFIGURATION_PATH = '/configuration'

const redirectConfig = {
  isDesktopVersion: false,
  redirects: [],
}

export const baseUrl = location.host
  ? `${location.protocol}//${location.host}`
  : location.origin

export const remoteProdDomain = 'struxhub.com'
export const remoteTestDomain = 'struxsite.com'

const defaultDesktopPort = '3000'
const defaultMobilePort = '3001'
export const configuredDesktopPort =
  typeof CONFIG !== 'undefined' && CONFIG.DESKTOP_PORT
    ? String(CONFIG.DESKTOP_PORT)
    : defaultDesktopPort
export const configuredMobilePort =
  typeof CONFIG !== 'undefined' && CONFIG.MOBILE_PORT
    ? String(CONFIG.MOBILE_PORT)
    : defaultMobilePort
const desktopPort = `:${configuredDesktopPort}`
const mobilePort = `:${configuredMobilePort}`

const { host } = location
export const isRemote = [remoteProdDomain, remoteTestDomain].some(d =>
  host.includes(d),
)

// Login hosts (login.struxsite.com, login.struxhub.com, preview env's
// <pr>.login.*) serve the single responsive frontend-login bundle —
// they don't have a separate mobile twin. Skip the auto-redirect
// computation so a mobile-UA user landing here doesn't get sent to a
// `login-mobile.*` host that no longer exists. Matches the same shape
// that nginx/default.conf already uses for the auth-host loop guard.
const LOGIN_HOST_RE = /^(login|[^.]+\.login)\.(struxhub\.com|struxsite\.com)$/

if (isRemote && !LOGIN_HOST_RE.test(location.host)) {
  const hostParts = location.host.split('.')
  const subdomain = hostParts[0]
  hostParts.splice(0, 1)
  const domain = hostParts.join('.')
  // Shared `-mobile` canonicalization (same helper the login portal and
  // Authentication.store use) — collapse the mobile twin to its tenant.
  const tenant = stripMobileSuffix(subdomain)
  const isMobileSubdomain = tenant !== subdomain

  const desktopUrl = `${tenant}.${domain}`
  const mobileUrl = `${tenant}-mobile.${domain}`

  if (!isMobileSubdomain) {
    redirectConfig.isDesktopVersion = true
    redirectConfig.redirects = [
      {
        from: desktopUrl,
        to: mobileUrl,
      },
    ]
  } else {
    redirectConfig.isDesktopVersion = false
    redirectConfig.redirects = [
      {
        from: mobileUrl,
        to: desktopUrl,
      },
    ]
  }
} else if (isRemote) {
  // Login host — single bundle, no mobile twin. Leave the default
  // (isDesktopVersion: false, redirects: []) so REDIRECT_TO_PROPER_SITE_VERSION
  // doesn't fire.
} else if (location.host.includes(desktopPort)) {
  redirectConfig.isDesktopVersion = true
  redirectConfig.redirects = [
    {
      from: desktopPort,
      to: mobilePort,
    },
  ]
} else {
  redirectConfig.isDesktopVersion = false
  redirectConfig.redirects = [
    {
      from: mobilePort,
      to: desktopPort,
    },
  ]
}

const TENANT_CONFIGURATION: IAppConfig = {
  REDIRECT_CONFIG: redirectConfig,
  BASE_URL: host.includes(remoteProdDomain)
    ? remoteProdDomain
    : remoteTestDomain,
} as IAppConfig

// Login hosts are tenant-agnostic (they render the static LoginConfig and
// never act as a tenant), so the remote /configuration endpoint has nothing
// to return there — the request just 404s and adds noise. Skip it on login
// hosts. NOTE: band-aid. The real fix is to make this remote load explicit
// (called only by the desktop/mobile bootstraps) instead of a module-load
// side effect, which also covers local-dev login hosts this regex misses.
TENANT_CONFIGURATION.READY =
  globalThis.document && !LOGIN_HOST_RE.test(host)
    ? fetch(REMOTE_CONFIGURATION_PATH)
        .then(response => response.json())
        .then(remoteConfig => {
          mapboxgl.accessToken = remoteConfig.MAPBOX_TOKEN
          mapboxgl.prewarm()

          Object.assign(
            TENANT_CONFIGURATION,
            remoteConfig,
            // CONFIG => build metadata injected via Vite `define` (REVISION, BUILD, etc.)
            typeof CONFIG !== 'undefined' ? CONFIG : {},
          )
        })
        .catch(error =>
          console.error(
            `We can not retrieve application configuration: ${error}`,
          ),
        )
    : Promise.resolve()

// Build metadata (VERSION, BUILD, REVISION, etc.) is injected via Vite `define`
// as the global `CONFIG` object. The desktop/mobile path merges it inside the
// remote-fetch callback above; the login path (which skips the fetch) needs it
// applied here so Config.VERSION is available in the portal sidebar.
if (typeof CONFIG !== 'undefined') {
  Object.assign(TENANT_CONFIGURATION, CONFIG)
}

export default TENANT_CONFIGURATION
